import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Star, Upload, ImageIcon } from 'lucide-react';
import { adminApi } from '../../lib/adminApi';

const inputCls = 'bg-slate-950 border-white/10 text-white placeholder:text-slate-500 rounded-xl';

export const TestimonialDialog = ({ testimonial, onClose, onSave}) => {
  const [form, setForm] = useState(testimonial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => { setForm(testimonial); setUploadError(''); }, [testimonial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    if (file.size > 5 * 1024 * 1024) { setUploadError('La imagen supera los 5 MB.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await adminApi.post(`/uploads/testimonial-photo`, fd);
      setForm((f) => ({ ...f, foto_url: data.url }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setUploadError(typeof detail === 'string' ? detail : 'No se pudo subir la imagen.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  if (!form) return null;

  return (
    <Dialog open={!!testimonial} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-slate-900 border-white/10 text-white sm:max-w-lg" data-testid="testimonial-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? 'Editar testimonio' : 'Nuevo testimonio'}</DialogTitle>
          <DialogDescription className="text-slate-400">Aparecerá en la página principal con foto y estrellas.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-xs">Nombre</Label>
              <Input data-testid="testimonial-nombre-input" required minLength={2} maxLength={80} value={form.nombre} onChange={set('nombre')} className={inputCls} placeholder="Ej: María Pérez" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Ciudad / detalle</Label>
              <Input data-testid="testimonial-ciudad-input" maxLength={80} value={form.ciudad || ''} onChange={set('ciudad')} className={inputCls} placeholder="Ej: Caracas, Venezuela" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Opinión</Label>
            <Textarea data-testid="testimonial-texto-input" required minLength={10} maxLength={600} rows={4} value={form.texto} onChange={set('texto')} className={inputCls} placeholder="¿Qué dijo el cliente?" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Foto del cliente (opcional)</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.foto_url
                ? <img src={form.foto_url} alt="Vista previa" data-testid="testimonial-foto-preview" className="h-14 w-14 rounded-full object-cover ring-2 ring-white/20" />
                : <div className="h-14 w-14 rounded-full bg-slate-800 flex items-center justify-center text-slate-500"><ImageIcon className="h-6 w-6" /></div>}
              <div className="flex-1 space-y-2">
                <label className={`inline-flex items-center gap-2 cursor-pointer rounded-xl border border-white/20 px-4 py-2 text-sm hover:bg-white/10 transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
                  <Upload className="h-4 w-4" /> {uploading ? 'Subiendo...' : 'Subir desde mi computadora'}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" data-testid="testimonial-foto-file" onChange={handleFile} className="hidden" />
                </label>
                <Input data-testid="testimonial-foto-input" maxLength={500} value={form.foto_url || ''} onChange={set('foto_url')} className={`${inputCls} h-9 text-xs`} placeholder="o pega una URL https://..." />
                {uploadError && <p className="text-xs text-red-400" data-testid="testimonial-upload-error">{uploadError}</p>}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Estrellas</Label>
            <div className="flex gap-1 mt-1" data-testid="testimonial-stars-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} data-testid={`testimonial-star-${n}`} onClick={() => setForm((f) => ({ ...f, estrellas: n }))} aria-label={`${n} estrellas`}>
                  <Star className={`h-7 w-7 transition-colors ${n <= form.estrellas ? 'fill-amber-400 text-amber-400' : 'text-slate-600 hover:text-amber-300'}`} />
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" data-testid="testimonial-activo-checkbox" checked={!!form.activo} onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))} className="h-4 w-4 rounded" />
            Visible en la página
          </label>
          <DialogFooter>
            <Button type="button" data-testid="testimonial-cancel-btn" variant="outline" onClick={onClose} className="rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">Cancelar</Button>
            <Button type="submit" data-testid="testimonial-save-btn" disabled={saving || uploading} className="rounded-xl bg-blue-600 hover:bg-blue-500">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TestimonialDialog;
